import React from "react";
import { Grid, Card, CardContent, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

export default function StatsSummaryCards({ summary, comparison, entity, metric }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('stats');
  const hideTimeBasedCards = entity === 'files' && metric === 'forms_by_file';

  const getTrendLabel = (trend) => {
    if (trend === 'increasing') return t('trendIncreasing');
    if (trend === 'decreasing') return t('trendDecreasing');
    return t('trendStable');
  };

  if (!summary) return null;

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUpIcon sx={{ color: colors.greenAccent[500] }} />;
      case 'decreasing':
        return <TrendingDownIcon sx={{ color: colors.redAccent[500] }} />;
      default:
        return <TrendingFlatIcon sx={{ color: colors.grey[500] }} />;
    }
  };

  const getGrowthColor = (rate) => {
    if (rate > 0) return colors.greenAccent[500];
    if (rate < 0) return colors.redAccent[500];
    return colors.grey[500];
  };

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Total Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
            borderRadius: '12px',
            border: `1px solid ${colors.blueAccent[600]}30`
          }}
        >
          <CardContent>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                mb: 1
              }}
            >
              {hideTimeBasedCards ? t('totalForms') : t('total')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.grey[100]
              }}
            >
              {summary.total || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Average Card - Forms by File'da anlamı yok, gizle */}
      {!hideTimeBasedCards && (
      <Grid item xs={12} sm={6} md={3}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
            borderRadius: '12px',
            border: `1px solid ${colors.greenAccent[600]}30`
          }}
        >
          <CardContent>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                mb: 1
              }}
            >
              Average per Period
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.grey[100]
              }}
            >
              {summary.average_per_period?.toFixed(2) || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      )}

      {/* Growth Rate Card - Forms by File'da anlamı yok, gizle */}
      {!hideTimeBasedCards && (
      <Grid item xs={12} sm={6} md={3}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.primary[700]} 0%, ${colors.primary[800]} 100%)`,
            borderRadius: '12px',
            border: `1px solid ${colors.primary[600]}30`
          }}
        >
          <CardContent>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                mb: 1
              }}
            >
              {t('growthRate')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: getGrowthColor(summary.growth_rate)
                }}
              >
                {summary.growth_rate > 0 ? '+' : ''}{summary.growth_rate?.toFixed(2) || 0}%
              </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
      )}

      {/* Trend Card - Forms by File'da anlamı yok, gizle */}
      {!hideTimeBasedCards && (
      <Grid item xs={12} sm={6} md={3}>
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.grey[700]} 0%, ${colors.grey[800]} 100%)`,
            borderRadius: '12px',
            border: `1px solid ${colors.grey[600]}30`
          }}
        >
          <CardContent>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                mb: 1
              }}
            >
              Trend
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getTrendIcon(summary.trend)}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: colors.grey[100],
                  textTransform: 'capitalize'
                }}
              >
                {getTrendLabel(summary.trend)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      )}

      {/* Comparison Card (if available) */}
      {comparison && (
        <Grid item xs={12}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.blueAccent[800]} 0%, ${colors.blueAccent[900]} 100%)`,
              borderRadius: '12px',
              border: `1px solid ${colors.blueAccent[700]}30`
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: colors.grey[100],
                  mb: 2
                }}
              >
                {t('comparisonWithPreviousPeriod')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.grey[300], mb: 0.5 }}
                  >
                    {t('previousTotal')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: colors.grey[100], fontWeight: 600 }}
                  >
                    {comparison.previous_total || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.grey[300], mb: 0.5 }}
                  >
                    Change (Absolute)
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: getGrowthColor(comparison.change_absolute),
                      fontWeight: 600
                    }}
                  >
                    {comparison.change_absolute > 0 ? '+' : ''}{comparison.change_absolute?.toFixed(2) || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.grey[300], mb: 0.5 }}
                  >
                    {t('changePercentage')}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: getGrowthColor(comparison.change_percentage),
                      fontWeight: 600
                    }}
                  >
                    {comparison.change_percentage > 0 ? '+' : ''}{comparison.change_percentage?.toFixed(2) || 0}%
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}
